/* eslint-disable max-lines */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AfterViewInit,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { saveAs } from 'file-saver-es';
import { ButtonModule } from 'primeng/button';

import { Table, TableLazyLoadEvent, TableModule } from 'primeng/table';
import { Subscription } from 'rxjs';

import { ActionClickedResponse } from 'src/app/models/ActionClickedResponse';
import { CellChangedResponse } from 'src/app/models/CellChangedResponse';
import { GenericActionConfiguration } from 'src/app/models/GenericActionConfiguration';
import { GenericRowActionConfiguration } from 'src/app/models/GenericRowActionConfiguration';
import { GenericTableConfiguration } from 'src/app/models/GenericTableConfiguration';
import { TableFilter } from 'src/app/models/TableFilter';
import { TableFilterService } from 'src/app/services/api/filter.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { MessageCenterService } from 'src/app/services/message-center.service';
import { SHARED_IMPORTS } from 'src/app/utils/other/standalone-import';
import { AppAction } from 'src/config/authorization.config';
import { environment } from 'src/environments/environment';
import { GenericColumnBodyComponent } from './generic-column-body/generic-column-body.component';
import { MultiSelectModule } from 'primeng/multiselect';
import { DatePickerModule } from 'primeng/datepicker';
import { TooltipModule } from 'primeng/tooltip';
import { PopoverModule } from 'primeng/popover';
import { FilterCreateComponent } from './filter/filter-create/filter-create.component';
import { FilterComponent } from './filter/filter.component';
import { TableActionComponent } from '../table-action/table-action.component';
import { FormsModule } from '@angular/forms';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
interface ExportColumn {
  title: string;
  dataKey: string;
}

@Component({
  selector: 'app-generic-table',
  templateUrl: './generic-table.component.html',
  styleUrls: ['./generic-table.component.scss'],
  standalone: true,
  imports: [
    SHARED_IMPORTS,
    ButtonModule,
    TableModule,
    GenericColumnBodyComponent,
    MultiSelectModule,
    DatePickerModule,
    TooltipModule,
    PopoverModule,
    // FilterComponent,
    FilterCreateComponent,
    TableActionComponent,
    FormsModule,
    IconField,
    InputIcon
  ]
})
export class GenericTableComponent implements OnInit, OnChanges, OnDestroy {
  private translate = inject(TranslateService);
  private tableFilterService = inject(TableFilterService);
  private messageCenterService = inject(MessageCenterService);
  private authService = inject(AuthService);

  @ViewChild('appFilter') appFilter?: FilterComponent;

  @ViewChild('dt') public dataTable?: Table;

  @Input() config!: GenericTableConfiguration;

  @Input() objectList: any[] = [];

  @Input() totalRecords = 0;

  @Output() actionClicked = new EventEmitter<ActionClickedResponse>();

  @Output() cellChanged = new EventEmitter<CellChangedResponse>();

  @Output() lazyLoadTableEventFired = new EventEmitter<TableLazyLoadEvent>();

  @Output() objectChanged = new EventEmitter<any>();

  @Output() selectionChanged = new EventEmitter<any[]>();

  displayOverlay = false;

  environment = environment;

  exportColumns!: ExportColumn[];

  globalFilterFields: string[] = [];

  globalFilterValue = '';

  lazyState?: TableLazyLoadEvent;

  loading = false;

  rowsPerPageOptions = environment.tableConfiguration.rowsPerPageOptions;

  saveTableFiltersEnabled = environment.saveTableFiltersEnabled;

  scrollHeight: string = environment.tableConfiguration.scrollHeight;

  selectedFilterId = 0;

  selectedObjects: any[] = [];

  styleClass = '';

  subscriptions = new Subscription();

  tableFilters: { [s: string]: { value: any } } = {};

  frozenColumns: string[] = [];

  ngOnInit() {
    this.generateStyleClass();
    this.generateFilterFields();
    this.generateExportColumns();
    if (!this.config.showFilterConfiguration) {
      this.saveTableFiltersEnabled = false;
    }
    this.config.columns.forEach((col) => {
      if (col.frozenLeft || col.frozenRight) {
        this.frozenColumns.push(col.field);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['objectList'] &&
      ((this.objectList && this.objectList.length) || this.totalRecords === 0)
    ) {
      this.loading = false;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /**
   * Generates the global filter fields for the table.
   * Filters the columns to include only those marked as global filter fields.
   * Appends additional global filter fields specified in the column configuration.
   *
   * @returns {void}
   */
  private generateFilterFields(): void {
    // Filter columns to include only those marked as global filter fields
    this.globalFilterFields = this.config.columns
      .filter((col) => col.isGlobalFilterField)
      .map((col) => col.field);

    // Append additional global filter fields specified in the column configuration
    this.config.columns.forEach((col) => {
      if (!col.appendGlobalFilterFields) {
        return;
      }

      this.globalFilterFields.push(...col.appendGlobalFilterFields);
    });
  }

  /**
   * Loads data for the table based on the lazy load event.
   * Sets the lazy state and loading flag, then emits the lazyLoadTableEventFired event.
   *
   * @param {TableLazyLoadEvent} event - The event object containing information about the lazy load request.
   * @returns {void}
   */
  loadData(event: TableLazyLoadEvent): void {
    // Set the lazy state and loading flag
    this.lazyState = event;
    this.loading = true;

    // Emit the lazyLoadTableEventFired event
    this.lazyLoadTableEventFired.emit(event);
  }

  /**
   * Generates the style class for the table based on the configuration.
   * Adds classes for striped rows, grid lines, and table size.
   *
   * @returns {void}
   */
  private generateStyleClass(): void {
    // Add class for striped rows if configured
    if (this.config.showStripedRows) {
      this.styleClass += ' p-datatable-striped';
    }

    // Add class for grid lines if configured
    if (this.config.showGridLines) {
      this.styleClass += ' p-datatable-gridlines';
    }

    // Add class for table size based on the configuration
    switch (this.config.size) {
      case 'sm':
        this.styleClass += ' p-datatable-sm';
        break;
      case 'lg':
        this.styleClass += ' p-datatable-lg';
        break;
      default:
        break;
    }
  }

  /**
   * Generates the export columns configuration for the table.
   * Maps the table columns to an array of objects with title and dataKey properties.
   *
   * @returns {void}
   */
  private generateExportColumns(): void {
    this.exportColumns = this.config.columns.map((col) => ({
      title: col.field,
      dataKey: col.field
    }));
  }

  /**
   * Handles the event when an object is changed.
   * Emits the objectChanged event with the provided object.
   *
   * @param {any} object - The object that has changed.
   * @returns {void}
   */
  onObjectChanged(object: any): void {
    this.objectChanged.emit(object);
  }

  /**
   * Handles the event when a cell is changed.
   * Creates a CellChangedResponse object with the changed cell's column and object.
   * Emits the cellChanged event with the response.
   *
   * @param {any} object - The object that has changed.
   * @param {string} field - The field (column) that has changed.
   * @returns {void}
   */
  onCellChanged(object: any, field: string): void {
    const changedCell = new CellChangedResponse();
    changedCell.column = field;
    changedCell.object = object;
    this.cellChanged.emit(changedCell);
  }

  /**
   * Handles the event when the selection is changed.
   * Emits the selectionChanged event with the provided list of selected items.
   *
   * @param {any} list - The list of selected items.
   * @returns {void}
   */
  onSelectionChanged(list: any): void {
    this.selectionChanged.emit(list);
  }

  /**
   * Handles the event when a column is clicked.
   * Creates an ActionClickedResponse object with the clicked object and action configuration.
   * Emits the actionClicked event with the response.
   *
   * @param {any} clickedObject - The object that was clicked.
   * @returns {void}
   */
  onColumnClicked(clickedObject: any): void {
    const response = new ActionClickedResponse();
    response.object = clickedObject;
    response.action = new GenericActionConfiguration({
      returnType: 'object',
      identifier: 'columnClicked'
    });
    this.actionClicked.emit(response);
  }

  /**
   * Handles the click event for an action.
   * Creates an ActionClickedResponse object and sets the action and object(s) based on the action configuration.
   * Emits the actionClicked event with the response.
   *
   * @param {GenericActionConfiguration | GenericRowActionConfiguration} action - The action configuration.
   * @param {any} [row] - The row data associated with the action, if applicable.
   * @returns {void}
   */
  onActionClicked(
    action: GenericActionConfiguration | GenericRowActionConfiguration,
    row?: any
  ): void {
    // Create a new ActionClickedResponse object
    const response = new ActionClickedResponse();
    response.action = action;

    // Handle GenericActionConfiguration actions
    if (action instanceof GenericActionConfiguration) {
      switch (action.returnType) {
        case 'selection':
          // Set the object list to the selected objects
          response.objectList = this.selectedObjects;
          break;
        case 'object':
          // Set the object to the provided row data
          if (row) {
            response.object = row;
          }
          break;
        default:
          break;
      }
    }

    // Handle GenericRowActionConfiguration actions
    if (action instanceof GenericRowActionConfiguration && row) {
      // Set the object to the provided row data
      response.object = row;
    }

    // Emit the actionClicked event with the response
    this.actionClicked.emit(response);
  }

  /**
   * Checks if the current user has the required permissions for a given action.
   * If no subject or action is provided, it returns true by default.
   *
   * @param {AppAction | AppAction[] | undefined} action - The action or actions to check permissions for.
   * @returns {boolean} - True if the user has the required permissions, false otherwise.
   */
  $can(action: AppAction | AppAction[] | undefined): boolean {
    // Get the permission subject from the configuration
    const subject = this.config.permissionSubject;

    // If no subject or action is provided, return true by default
    if (!subject || !action) {
      return true;
    }

    // Check if the user has the required permissions for the given action and subject
    return this.authService.$can(action, subject);
  }

  /**
   * Handles the click event to save a filter.
   * Creates a new TableFilter object with the current filter data and table configuration.
   * Subscribes to the tableFilterService to save the filter.
   * On success, reloads the filter data and shows a success toast message.
   * On error, shows an error toast message.
   *
   * @param {string} filterName - The name of the filter to be saved.
   * @returns {void}
   */
  saveFilterClicked(filterName: string): void {
    if (this.dataTable) {
      // Create a new TableFilter object
      const tableFilter = new TableFilter();

      // Set the filter data and table configuration
      tableFilter.filterData = this.dataTable.filters;
      tableFilter.table = this.config.table;
      tableFilter.filterName = filterName;

      // Subscribe to the tableFilterService to save the filter
      this.subscriptions.add(
        this.tableFilterService.create(tableFilter).subscribe({
          next: (value: TableFilter) => {
            if (value) {
              // Reload the filter data and show a success toast message
              this.appFilter?.reloadData();
              this.messageCenterService.showToast(
                this.translate.instant(
                  'filterComponent.actions.toasts.create.success.summary'
                ),
                this.translate.instant(
                  'filterComponent.actions.toasts.create.success.detail'
                ),
                'success'
              );
            }
          },
          error: (error) => {
            if (error) {
              // Show an error toast message
              this.messageCenterService.showToast(
                this.translate.instant(
                  'filterComponent.actions.toasts.create.error.summary'
                ),
                this.translate.instant(
                  'filterComponent.actions.toasts.create.error.detail'
                ),
                'error'
              );
            }
          }
        })
      );
    }
  }

  /**
   * Applies the selected filter to the table.
   * Updates the selected filter ID and applies the filter data to the data table.
   * If lazy loading is enabled, it updates the lazy state filters and reloads the data.
   * Otherwise, it directly filters the data table.
   *
   * @param {TableFilter} filter - The filter to be applied.
   * @returns {void}
   */
  filterApplied(filter: TableFilter): void {
    // Update the selected filter ID
    this.selectedFilterId = filter.id;

    // Check if the data table exists
    if (this.dataTable) {
      // Apply the filter data to the data table
      this.dataTable.filters = filter.filterData as any;

      // If lazy loading is not enabled, directly filter the data table
      if (!this.lazyState) {
        this.dataTable._filter();
      }
    }

    // If lazy loading is enabled and filters exist in the lazy state
    if (this.lazyState && this.lazyState.filters) {
      // Update the lazy state filters and reload the data
      this.lazyState.filters = filter.filterData as any;
      this.loadData(this.lazyState);
    }
  }

  /**
   * Clears all filters applied to the table.
   * Resets the selected filter ID to 0 and clears filter values in the data table.
   * If lazy loading is enabled, it updates the lazy state filters and reloads the data.
   * Otherwise, it resets the data table.
   * Also removes the table memory from local storage.
   *
   * @returns {void}
   */
  clearFilters(): void {
    // Reset the selected filter ID
    this.selectedFilterId = 0;

    // Check if the data table exists
    if (this.dataTable) {
      // Clear filter values in the data table
      this.dataTable.clearFilterValues();

      // Check if lazy loading is enabled
      if (this.lazyState) {
        // Update the lazy state filters and reload the data
        this.lazyState.filters = this.dataTable.filters;
        this.loadData(this.lazyState);
      } else {
        // Reset the data table
        this.dataTable.reset();
      }
    }

    // Remove the table memory from local storage
    localStorage.removeItem(`${this.config.table}-table-memory`);
  }

  /**
   * Toggles the display state of the overlay.
   * If the overlay is currently displayed, it hides it.
   * If the overlay is currently hidden, it shows it.
   *
   * @returns {void}
   */
  onOverlayDisplayChange(): void {
    // Toggle the display state of the overlay
    this.displayOverlay = !this.displayOverlay;
  }

  /**
   * Exports the table data to a PDF file.
   * Uses the jsPDF and jsPDF-AutoTable libraries to generate the PDF.
   * If there are selected objects, it exports only the selected objects.
   * Otherwise, it exports the entire object list.
   *
   * @returns {void}
   */
  exportPdf(): void {
    // Dynamically import the jsPDF library
    import('jspdf').then((jsPDF) => {
      // Dynamically import the jsPDF-AutoTable plugin
      import('jspdf-autotable').then(() => {
        // Create a new jsPDF document in portrait mode, with pixel units, and A4 size
        const doc = new jsPDF.default('p', 'px', 'a4');
        // Generate the table in the PDF document
        (doc as any).autoTable(
          this.exportColumns,
          this.selectedObjects.length > 0
            ? this.selectedObjects
            : this.objectList
        );
        // Save the generated PDF with the name 'export.pdf'
        doc.save('export.pdf');
      });
    });
  }

  /**
   * Exports the table data to an Excel file.
   * Uses the xlsx library to generate the Excel file.
   * If there are selected objects, it exports only the selected objects.
   * Otherwise, it exports the entire object list.
   *
   * @returns {void}
   */
  exportExcel(): void {
    // Dynamically import the xlsx library
    import('xlsx').then((xlsx) => {
      // Convert the data to a worksheet
      const worksheet = xlsx.utils.json_to_sheet(
        this.selectedObjects.length > 0 ? this.selectedObjects : this.objectList
      );
      // Create a workbook with the worksheet
      const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
      // Write the workbook to a buffer
      const excelBuffer: any = xlsx.write(workbook, {
        bookType: 'xlsx',
        type: 'array'
      });
      // Save the buffer as an Excel file
      this.saveAsExcelFile(excelBuffer, '');
    });
  }

  /**
   * Saves the provided buffer as an Excel file.
   * Uses the FileSaver library to save the file.
   *
   * @param {any} buffer - The buffer containing the Excel file data.
   * @param {string} fileName - The base name for the saved file.
   * @returns {void}
   */
  saveAsExcelFile(buffer: any, fileName: string): void {
    const EXCEL_TYPE =
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const EXCEL_EXTENSION = '.xlsx';
    // Create a Blob from the buffer with the appropriate MIME type
    const data: Blob = new Blob([buffer], {
      type: EXCEL_TYPE
    });
    // Save the Blob as a file with the specified name and current timestamp
    saveAs(
      data,
      `${fileName}_export_${new Date().getTime()}${EXCEL_EXTENSION}`
    );
  }
}
